import React from "react";
import {useKeycloak} from "@react-keycloak/web";
import {useTranslation} from "react-i18next";
// Material UI imports
import {
    Box,
    Container,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
} from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
// Local imports
import InputVariableNew from "./InputVariableNew";
// Project imports
import {AddCircleActionButton, EditLinkActionButton, IdCopyButton, RefreshActionButton} from "../../common/buttons";
import {ServerCommunicationAlert} from "../../common/errors";
import {Loading} from "../../common/Loading";
import {InfoNotification} from "../../common/notifications";
import {InputVariableDTO} from "../../../models/library";
import ROUTES from "../../../routes/routes";
import {useStyles} from "../../../styles";
import {API, createApiConfig} from "../../../utils";
import {AxiosError} from "axios";
import {Page} from "../../../models/pagination";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";

interface InputVariableRowProps {
    inputVariable: InputVariableDTO;
}

const InputVariableRow: React.FC<InputVariableRowProps> = (props: InputVariableRowProps) => {
    return (
        <TableRow>
            <TableCell>{props.inputVariable.name}</TableCell>
            <TableCell align="right">
                <IdCopyButton text={props.inputVariable.id} size="small" />
                <EditLinkActionButton to={ROUTES.inputVariable.create(props.inputVariable.id)} size="small" />
            </TableCell>
        </TableRow>
    );
};

export const InputVariablesTable: React.FC = () => {
    //== Init ===================================================================
    const [t] = useTranslation("libraries");
    const classes = useStyles();
    const {keycloak, initialized} = useKeycloak();
    const initState = {
        page: {
            size: 50,
            totalElements: 0,
            totalPages: 0,
            number: 0,
        },
        query: "",
        units: [] as InputVariableDTO[],
        name: "loading",
        action: "init",
    };
    const [state, setState] = React.useState(initState);
    //== Effects ================================================================
    React.useEffect(() => {
        if (state.name === "loading") {
            let url = `/input-variables?page=${state.page.number}&size=${state.page.size}&sort=variable.name,asc`;
            if (state.query !== "") {
                url += `&query=${state.query}`;
            }
            API.get<Page<InputVariableDTO>>(url, createApiConfig(keycloak, initialized))
                .then((res) => {
                    setState({
                        ...state,
                        page: res.data,
                        units: res.data?.content ?? [],
                        name: "loaded",
                    });
                })
                .catch((err: AxiosError) => {
                    setState({...state, name: "failed"});
                });
        }
    }, [keycloak, initialized, state, setState]);
    //== Handlers ===============================================================
    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number): void => {
        setState({
            ...state,
            page: {...state.page, number: value - 1},
            name: "loading",
            action: "init",
        });
    };
    const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setState({...state, query: event.target.value});
    };
    const handleSearchSubmit = (): void => {
        setState({...state, name: "loading"});
    };
    const handleCreateClick = (): void => setState({...state, name: "creating"});
    //== Render =================================================================
    if (state.name === "loading") {
        return <Loading />;
    }
    if (state.name === "creating") {
        return <InputVariableNew />;
    }
    if (state.name === "failed") {
        return <ServerCommunicationAlert />;
    }
    // - default
    let notification: JSX.Element | null = null;
    if (state.name === "loaded" && state.action === "refresh") {
        notification = <InfoNotification message={t("definitions.input_variables.notifications.refresh_ok")} />;
    }
    const rows = state.units.map((inputVariable: InputVariableDTO) => (
        <InputVariableRow key={inputVariable.id} inputVariable={inputVariable} />
    ));
    return (
        <Container maxWidth="lg">
            <Box display="flex" justifyContent="center">
                <Box flexGrow={1}>
                    {state.page.totalPages > 1 && (
                        <Pagination
                            style={{marginTop: "1em"}}
                            count={state.page.totalPages}
                            page={state.page.number + 1}
                            onChange={handlePageChange}
                        />
                    )}
                </Box>
                <form onSubmit={handleSearchSubmit} style={{width: "auto"}}>
                    <Box display="flex" alignItems="center" justifyContent="center">
                        <TextField
                            value={state.query}
                            onChange={handleQueryChange}
                            label={t("definitions.units.query")}
                            margin="dense"
                            variant="outlined"
                            style={{minWidth: "20em", marginRight: "1em"}}
                        />
                        <IconButton onClick={handleSearchSubmit} size="small">
                            <SearchIcon />
                        </IconButton>
                        <IconButton onClick={handleCreateClick} size="small">
                            <AddIcon />
                        </IconButton>
                    </Box>
                </form>
            </Box>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell align="left">{t("definitions.input_variables.name")}</TableCell>
                        <TableCell align="right" style={{minWidth: "8em"}}>
                            <RefreshActionButton
                                size="small"
                                onClick={(): void =>
                                    setState({
                                        ...initState,
                                        action: "refresh",
                                    })
                                }
                            />
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>{rows}</TableBody>
            </Table>
            {state.page.totalPages > 1 && (
                <Pagination
                    style={{marginTop: "1em"}}
                    count={state.page.totalPages}
                    page={state.page.number + 1}
                    onChange={handlePageChange}
                />
            )}
            {notification}
            <div className={classes.fabWrapper}>
                <AddCircleActionButton onClick={(): void => setState({...state, name: "creating"})} />
            </div>
        </Container>
    );
};

export default InputVariablesTable;
